import { useEffect, useState } from 'react'
import { applyScale, applyTheme, currentScale, currentTheme, type MioScale, type MioTheme } from '../lib/theme'
import { useI18n } from '../i18n/useI18n'
import type { AppStatus } from '../types'
import { legalDocumentUrl } from '../lib/legalLocale'

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface StandaloneNavigator extends Navigator {
  standalone?: boolean
}

const PWA_INSTALLED_STORAGE_KEY = 'miosegno-pwa-installed'

const themes: Array<{ value: MioTheme; labelKey: string }> = [
  { value: 'white', labelKey: 'settings.theme.white' },
  { value: 'black', labelKey: 'settings.theme.black' },
  { value: 'blue', labelKey: 'settings.theme.blue' },
  { value: 'pink', labelKey: 'settings.theme.pink' },
]
const scales: MioScale[] = [67, 80, 90, 100, 110, 125, 140]

function runningAsInstalledApp(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || Boolean((navigator as StandaloneNavigator).standalone)
}

function rememberedInstalledApp(): boolean {
  try {
    return localStorage.getItem(PWA_INSTALLED_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function rememberInstalledApp(installed: boolean) {
  try {
    if (installed) localStorage.setItem(PWA_INSTALLED_STORAGE_KEY, 'true')
    else localStorage.removeItem(PWA_INSTALLED_STORAGE_KEY)
  } catch {
    // 저장소 접근이 막힌 브라우저에서도 설치·업데이트 버튼 자체는 계속 동작합니다.
  }
}

export function SettingsPanel({ appStatus }: { appStatus: AppStatus | null }) {
  const { locale, locales, setLocale, t } = useI18n()
  const [theme, setTheme] = useState<MioTheme>(currentTheme())
  const [scale, setScale] = useState<MioScale>(currentScale())
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(() => runningAsInstalledApp() || rememberedInstalledApp())
  const [installMessageKey, setInstallMessageKey] = useState(() => runningAsInstalledApp()
    ? 'settings.app.runningInstalled'
    : 'settings.app.installAvailable')

  useEffect(() => {
    if (runningAsInstalledApp()) {
      setInstalled(true)
      rememberInstalledApp(true)
    }

    const listener = (event: Event) => {
      event.preventDefault()
      // beforeinstallprompt가 다시 왔다는 것은 이 브라우저에서 설치가 가능하다는 뜻입니다.
      // 과거 설치 기록만 남아 있는 경우를 정리해 설치 버튼을 다시 보여 줍니다.
      setInstalled(false)
      rememberInstalledApp(false)
      setInstallPrompt(event as InstallPromptEvent)
    }
    const installedListener = () => {
      setInstalled(true)
      rememberInstalledApp(true)
      setInstallPrompt(null)
      setInstallMessageKey('settings.app.installedNow')
    }
    const displayMode = window.matchMedia('(display-mode: standalone)')
    const displayModeListener = (event: MediaQueryListEvent) => {
      if (!event.matches) return
      setInstalled(true)
      rememberInstalledApp(true)
      setInstallMessageKey('settings.app.runningInstalled')
    }

    window.addEventListener('beforeinstallprompt', listener)
    window.addEventListener('appinstalled', installedListener)
    displayMode.addEventListener?.('change', displayModeListener)
    return () => {
      window.removeEventListener('beforeinstallprompt', listener)
      window.removeEventListener('appinstalled', installedListener)
      displayMode.removeEventListener?.('change', displayModeListener)
    }
  }, [])

  async function install() {
    if (installPrompt) {
      await installPrompt.prompt()
      const choice = await installPrompt.userChoice
      setInstallMessageKey(choice.outcome === 'accepted' ? 'settings.app.installStarted' : 'settings.app.installCancelled')
      setInstallPrompt(null)
      return
    }
    const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setInstallMessageKey(isiOS
      ? 'settings.app.instructionsIos'
      : 'settings.app.instructionsBrowser')
  }

  async function updateApp() {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration) await registration.update()
      }
      setInstallMessageKey('settings.app.updateChecked')
      window.setTimeout(() => location.reload(), 250)
    } catch {
      setInstallMessageKey('settings.app.updateFailed')
    }
  }

  return (
    <section className="card settings-card">
      <p className="eyebrow">{t('settings.eyebrow')}</p>
      <h2>{t('settings.title')}</h2>

      <h3>{t('settings.language.title')}</h3>
      <label>
        <span className="muted">{t('settings.language.description')}</span>
        <select
          value={locale}
          onChange={(event) => setLocale(event.target.value)}
          aria-label={t('settings.language.ariaLabel')}
        >
          {locales.map((item) => (
            <option key={item.locale} value={item.locale}>{item.name}</option>
          ))}
        </select>
      </label>

      <h3>{t('settings.theme.title')}</h3>
      <div className="choice-row">
        {themes.map((item) => (
          <button
            key={item.value}
            className={theme === item.value ? 'choice active' : 'choice'}
            onClick={() => {
              setTheme(item.value)
              applyTheme(item.value)
            }}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>

      <h3>{t('settings.scale.title')}</h3>
      <div className="choice-row">
        {scales.map((value) => (
          <button
            key={value}
            className={scale === value ? 'choice active' : 'choice'}
            onClick={() => {
              setScale(value)
              applyScale(value)
            }}
          >
            {value}%
          </button>
        ))}
      </div>

      <h3>{t('settings.app.title')}</h3>
      <button
        className="button secondary"
        onClick={() => void (installed ? updateApp() : install())}
      >
        {installed ? t('settings.app.update') : t('settings.app.install')}
      </button>
      <p className="muted">{t(installMessageKey)}</p>
      <h3>{t('settings.legal.title')}</h3>
      <div className="settings-legal-links">
        {appStatus?.serviceTermsUrl ? <a className="button ghost" href={legalDocumentUrl(appStatus.serviceTermsUrl, locale)} target="_blank" rel="noreferrer">{t('legal.serviceTerms')}</a> : <span className="muted">{t('settings.legal.serviceTermsMissing')}</span>}
        {appStatus?.privacyPolicyUrl ? <a className="button ghost" href={legalDocumentUrl(appStatus.privacyPolicyUrl, locale)} target="_blank" rel="noreferrer">{t('legal.privacy')}</a> : <span className="muted">{t('settings.legal.privacyMissing')}</span>}
        {appStatus?.pricingPolicyUrl ? <a className="button ghost" href={appStatus.pricingPolicyUrl} target="_blank" rel="noreferrer">{t('legal.pricingFormal')}</a> : <span className="muted">{t('settings.legal.pricingMissing')}</span>}
        {appStatus?.copyrightPolicyUrl ? <a className="button ghost" href={appStatus.copyrightPolicyUrl} target="_blank" rel="noreferrer">{t('legal.copyright')}</a> : <span className="muted">{t('settings.legal.copyrightMissing')}</span>}
      </div>
      <p className="muted">{t('settings.privacyNotice')}</p>
    </section>
  )
}
